'use client';

import { useCallback, useLayoutEffect, useRef } from 'react';

import type { SeoResolveData } from '@/features/seo-listing/types';
import { applySeoResolveDocumentMeta } from '@/features/seo-listing/utils';

/**
 * Keeps catalog SEO payload so soft-reset / empty filters can restore chrome
 * without another resolve round-trip.
 */
export const useListingCatalogSeoRestore = (
  basePath: string,
  initialSeoPage: SeoResolveData | null | undefined,
  setSeoPage: (page: SeoResolveData | null) => void,
  setSeoPageTitle: (title: string | null) => void,
) => {
  const isCatalogListingSeoPage = useCallback(
    (page: SeoResolveData | null | undefined): page is SeoResolveData => {
      if (!page) return false;
      // Runtime resolve may return listing chrome for catalog routes.
      return (
        (page.pageType as string) === 'listing' &&
        (page.permanentUrl === basePath || page.permanentUrl === `${basePath}/`)
      );
    },
    [basePath],
  );

  const catalogSeoPageRef = useRef<SeoResolveData | null>(
    isCatalogListingSeoPage(initialSeoPage) ? initialSeoPage : null,
  );

  useLayoutEffect(() => {
    if (isCatalogListingSeoPage(initialSeoPage)) {
      catalogSeoPageRef.current = initialSeoPage;
    }
  }, [initialSeoPage, isCatalogListingSeoPage]);

  const restoreCatalogSeoPage = useCallback((): void => {
    const catalog = catalogSeoPageRef.current;
    setSeoPage(catalog);
    setSeoPageTitle(catalog?.title ?? null);
    if (catalog) {
      applySeoResolveDocumentMeta(catalog);
    }
  }, [setSeoPage, setSeoPageTitle]);

  return { catalogSeoPageRef, restoreCatalogSeoPage, isCatalogListingSeoPage };
};
